/**
 * Prompts module - MCP prompt registration and handling
 */

import type { Prompt, GetPromptResult } from "@modelcontextprotocol/sdk/types.js";
import {
  flowDesignPrompt,
  generateFlowDesignMessages,
  type FlowDesignArgs,
} from "./flow-design.js";
import {
  debugExecutionPrompt,
  generateDebugExecutionMessages,
  type DebugExecutionArgs,
} from "./debug-execution.js";
import {
  migrateFlowsPrompt,
  generateMigrateFlowsMessages,
  type MigrateFlowsArgs,
} from "./migrate-flows.js";

// ============================================================================
// Registered Prompts
// ============================================================================

export const prompts: Prompt[] = [
  flowDesignPrompt,
  debugExecutionPrompt,
  migrateFlowsPrompt,
];

// ============================================================================
// Prompt Execution
// ============================================================================

type PromptArgs = Record<string, string>;

/**
 * Get prompt messages for a given prompt name and arguments
 */
export function getPrompt(
  name: string,
  args: PromptArgs
): GetPromptResult | null {
  switch (name) {
    case "flow-design": {
      const flowArgs: FlowDesignArgs = {
        goal: args.goal ?? "",
        triggers: args.triggers,
        connectors: args.connectors,
        complexity: args.complexity as FlowDesignArgs["complexity"],
      };
      return {
        description: flowDesignPrompt.description,
        messages: generateFlowDesignMessages(flowArgs),
      };
    }

    case "debug-execution": {
      const debugArgs: DebugExecutionArgs = {
        flowId: args.flowId ?? "",
        runId: args.runId,
        errorMessage: args.errorMessage,
        failedAction: args.failedAction,
      };
      return {
        description: debugExecutionPrompt.description,
        messages: generateDebugExecutionMessages(debugArgs),
      };
    }

    case "migrate-flows": {
      const migrateArgs: MigrateFlowsArgs = {
        flowId: args.flowId ?? "",
        sourceEnvironment: args.sourceEnvironment ?? "",
        targetEnvironment: args.targetEnvironment ?? "",
        connectionMapping: args.connectionMapping,
        newDisplayName: args.newDisplayName,
      };
      return {
        description: migrateFlowsPrompt.description,
        messages: generateMigrateFlowsMessages(migrateArgs),
      };
    }

    default:
      return null;
  }
}

// ============================================================================
// Re-exports
// ============================================================================

export {
  flowDesignPrompt,
  generateFlowDesignMessages,
  getQuickDesignTemplates,
  type FlowDesignArgs,
} from "./flow-design.js";

export {
  debugExecutionPrompt,
  generateDebugExecutionMessages,
  findQuickFix,
  getErrorPatterns,
  type DebugExecutionArgs,
} from "./debug-execution.js";

export {
  migrateFlowsPrompt,
  generateMigrateFlowsMessages,
  getMigrationChecklist,
  type MigrateFlowsArgs,
} from "./migrate-flows.js";
